// Lets the user pick which enrichment to run, then shows a descriptive
// loading state while the Claude call (web-search-grounded, can take up to
// a minute) is in flight — replacing a bare spinner on the header button,
// which read as the button having silently hung.

import React from "react";

import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";

const LOADING_COPY = {
	full: { title: "Researching and describing this property…", detail: "Looking up property facts and builder/management companies, then writing a description." },
	research: { title: "Researching property details…", detail: "Checking counts, cladding, roof type, and builder/management companies against the web." },
	description: { title: "Generating description…", detail: "Writing a description grounded in this property's known details." },
};

const PropertyEnrichRunDialog = ({ open, isFirstEnrich, running, error, onClose, onCancelRun, onRunFull, onRunResearch, onRunDescription }) => (
	<Dialog open={open} onClose={running ? undefined : onClose} maxWidth="sm" fullWidth>
		<DialogTitle className="propertyViewerEditDialogTitle">Enrich Property</DialogTitle>
		<DialogContent className="propertyViewerEditDialogContent">
			{running ? (
				<div className="propertyViewerEnrichLoading">
					<CircularProgress size={32} />
					<Typography variant="subtitle1" sx={{ mt: 2 }}>
						{LOADING_COPY[running]?.title || "Working…"}
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						{LOADING_COPY[running]?.detail}
					</Typography>
					<Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
						This can take up to a minute. Claude is searching live web sources for grounding.
					</Typography>
					<div className="dialogButtons">
						<Button variant="outlined" color="primary" onClick={onCancelRun}>
							Cancel
						</Button>
					</div>
				</div>
			) : isFirstEnrich ? (
				<>
					<Typography variant="body2" sx={{ mb: 2 }}>
						Runs Claude web-search research to fill in property details (year built, unit counts, cladding/roof
						type, builder and management companies) and generates a description, all in one pass.
					</Typography>
					{error && (
						<Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
							{error}
						</Typography>
					)}
					<div className="dialogButtons">
						<Button variant="outlined" color="primary" onClick={onClose}>
							Cancel
						</Button>
						<Button variant="contained" color="primary" disableElevation onClick={onRunFull}>
							Run Enrichment
						</Button>
					</div>
				</>
			) : (
				<>
					<div className="propertyViewerEnrichOptionStack">
						<Button variant="outlined" onClick={onRunDescription} className="propertyViewerEnrichOptionButton">
							<span className="propertyViewerEnrichOptionTitle">Regenerate Description</span>
							<span className="propertyViewerEnrichOptionDetail">Rewrite the description using the property's current details.</span>
						</Button>
						<Button variant="outlined" onClick={onRunResearch} className="propertyViewerEnrichOptionButton">
							<span className="propertyViewerEnrichOptionTitle">Re-run Research</span>
							<span className="propertyViewerEnrichOptionDetail">Re-check counts, cladding, roof type, and companies against the web.</span>
						</Button>
					</div>
					{error && (
						<Typography variant="caption" color="error" sx={{ display: "block", mt: 1.5 }}>
							{error}
						</Typography>
					)}
					<div className="dialogButtons">
						<Button variant="outlined" color="primary" onClick={onClose}>
							Cancel
						</Button>
					</div>
				</>
			)}
		</DialogContent>
	</Dialog>
);

export default PropertyEnrichRunDialog;
